import React from 'react';
import { message, Modal, Text, breakpoints, Link } from 'components';
import { SetupBrokerageWizardModalContext, AuthContext } from 'contexts';
import { VerticalSpace } from '@brokerbay/design-components';
import errorReporter from '@app/errorReporter';
import { UPGRADE_EMAIL } from 'utils';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Axios from 'axios';
import { PriceMap } from 'api/get';
import { StyledAlert, StyledHeading } from '../../constants';
import YourPlanInfo from './YourPlanInfo';
import PaymentMethod from './PaymentMethod';
import PricingInfo from './PricingInfo';

type PaymentInfoModalProps = {
  closeModal: () => void;
  goBack: () => void;
  priceMap: PriceMap;
};

const PaymentInfoModal: React.FC<PaymentInfoModalProps> = ({
  closeModal,
  goBack,
  priceMap,
}) => {
  const [cardStored, setCardStored] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { openModal: openSetupBrokerageWizardModal } = React.useContext(
    SetupBrokerageWizardModalContext,
  );
  const { fetchAuth } = React.useContext(AuthContext);

  const handleSubmit = async () => {
    if (loading) return;

    try {
      setLoading(true);
      const response = await Axios.post('plan/upgrade');
      if (response.data) {
        closeModal();
        message.success(
          'Payment details saved. Select your launch date below.',
        );
        await fetchAuth();
        openSetupBrokerageWizardModal();
      }
    } catch (error) {
      errorReporter.error(error);
      message.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    Modal.confirm({
      cancelText: 'No',
      content: 'Any unsaved changes will be lost.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      title: 'Close without saving?',
      onOk: closeModal,
    });
  };

  return (
    <Modal
      open
      title="Payment Info"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        disabled: !cardStored,
        ...{ 'data-cy': 'payment-info-submit-button' },
      }}
      cancelText="Back"
      onCancel={handleCancel}
      cancelButtonProps={{
        onClick: goBack,
      }}
      centered
      confirmLoading={loading}
      width={breakpoints.sm}
      destroyOnClose
    >
      <VerticalSpace size={8}>
        <StyledHeading strong block>
          Add your payment info
        </StyledHeading>
        <Text block type="secondary">
          Enter your credit card information below. This card will be charged on
          a monthly basis for any outstanding balances.
        </Text>
        <StyledAlert
          type="info"
          banner
          message={
            <Text>
              Have any questions about your billing info? Contact customer
              support at
              <Link href={`mailto:${UPGRADE_EMAIL}`}> {UPGRADE_EMAIL}</Link>
            </Text>
          }
        />
        <YourPlanInfo monthlyPrice={priceMap?.monthlyPrice} />
        <PaymentMethod setCardStored={setCardStored} />
        <PricingInfo priceMap={priceMap} />
        <Text
          type="secondary"
          textAlign="center"
          style={{ fontSize: '12px', margin: '20px 0px' }}
        >
          Your first month will be billed on the launch date you select on the
          next screen.
        </Text>
      </VerticalSpace>
    </Modal>
  );
};

export default PaymentInfoModal;
